import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {formatDate} from '@angular/common';
import {combineLatest, map} from 'rxjs';
import {Dropdown} from 'primeng/dropdown';
import {ActivatedRoute} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {PyramidCategory} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {TeamDynamicFacade} from '../../facade/team-dynamic.facade';
import {TeamDynamicRequestDto} from '../../dto/team-dynamic-request.dto';
import {TeamOverviewFilters} from '../../components/team-overview/team-overview.component';
import {StatementFilters} from '../../components/statements/statements.component';
import {StatementVm} from '../../models/team-dynamic.vm';

@Component({
  selector: 'app-team-dynamic-main-page',
  templateUrl: './team-dynamic-main-page.component.html',
  styleUrls: ['./team-dynamic-main-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamDynamicMainPageComponent implements OnInit {
  @ViewChild('periodDropdown') periodDropdown: Dropdown | undefined;

  readonly vm$ = combineLatest([
    this.teamDynamicFacade.loading$,
    this.teamDynamicFacade.overallScore$,
    this.teamDynamicFacade.pyramidCategories$,
    this.teamDynamicFacade.pyramidLegend$,
    this.teamDynamicFacade.scoreDevelopment$,
    this.teamDynamicFacade.loadingScoreDevelopment$,
    this.teamDynamicFacade.scoreDevelopmentChips$,
    this.teamDynamicFacade.teamOverview$,
    this.teamDynamicFacade.loadingTeamOverview$
  ]).pipe(
    map(([loading, overallScore, pyramidCategories, pyramidLegend, scoreDevelopment, loadingScoreDevelopment,
           scoreDevelopmentChips, teamOverview, loadingTeamOverview]) => ({
      loading,
      overallScore,
      pyramidCategories,
      pyramidLegend,
      scoreDevelopment,
      loadingScoreDevelopment,
      scoreDevelopmentChips,
      teamOverview,
      loadingTeamOverview
    }))
  );

  readonly indicatorVm$ = combineLatest([
    this.teamDynamicFacade.indicatorTabs$,
    this.teamDynamicFacade.selectedCategoryUuid$,
    this.teamDynamicFacade.indicatorDetail$,
    this.teamDynamicFacade.indicatorScoreDevelopment$,
    this.teamDynamicFacade.loadingIndicator$,
    this.teamDynamicFacade.statements$,
    this.teamDynamicFacade.loadingStatements$,
    this.teamDynamicFacade.statementResponses$,
    this.teamDynamicFacade.teamVisibility$
  ]).pipe(
    map(([tabs, selectedUuid, detail, scoreDevelopment, loading, statements, loadingStatements,
           statementResponses, teamVisibility]) => ({
      tabs, selectedUuid, detail, scoreDevelopment, loading, statements, loadingStatements,
      statementResponses, teamVisibility
    }))
  );

  public view: 'overall' | 'indicator' = 'overall';
  public period: 'all' | 'custom' = 'all';
  public periodLabel: string = '';
  public periodOptions: { label: string, value: string }[] = [];
  public periodRangeDates: Date[] | null = null;
  public showPeriodCalendar: boolean = false;
  public activeChipUuid: string | null = null;

  private requestPayload: TeamDynamicRequestDto = {
    startDate: undefined,
    endDate: undefined,
    timedQuestionCollectionUuid: null,
    teamsCollectionUuid: null,
    teamsSort: 'DESC',
    statementsSort: null
  };

  constructor(private teamDynamicFacade: TeamDynamicFacade, private translate: TranslateService,
              private activeRoute: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.setPeriodOptions();
    this.setPeriodLabel();
    const category = this.activeRoute.snapshot.queryParamMap.get('category');
    if (category) {
      this.view = 'indicator';
    }
    this.teamDynamicFacade.loadData(this.requestPayload, category);
  }

  setView(view: 'overall' | 'indicator') {
    this.view = view;
    if (view === 'indicator') {
      this.teamDynamicFacade.openIndicatorView(this.requestPayload);
    }
  }

  onPyramidCategory(category: PyramidCategory) {
    this.view = 'indicator';
    this.teamDynamicFacade.selectIndicatorCategory(category.uuid, this.requestPayload);
  }

  onIndicatorCategory(collectionUuid: string) {
    this.teamDynamicFacade.selectIndicatorCategory(collectionUuid, this.requestPayload);
  }

  onPeriodChange(event: any) {
    if (event.value === 'all') {
      this.selectAllTime();
    }
  }

  selectAllTime() {
    this.period = 'all';
    this.periodRangeDates = null;
    this.showPeriodCalendar = false;
    this.setPeriodLabel();
    this.requestPayload = {...this.requestPayload, startDate: undefined, endDate: undefined};
    this.teamDynamicFacade.reloadForPeriod(this.requestPayload);
  }

  submitPeriodRange() {
    const range = this.periodRangeDates;
    if (!range || range.length !== 2 || !range[1]) {
      return;
    }
    this.period = 'custom';
    this.setPeriodLabel();
    this.periodDropdown?.hide();
    this.requestPayload = {
      ...this.requestPayload,
      startDate: formatDate(range[0], 'dd.MM.yyyy', 'en-US'),
      endDate: formatDate(range[1], 'dd.MM.yyyy', 'en-US')
    };
    this.teamDynamicFacade.reloadForPeriod(this.requestPayload);
  }

  clearPeriodRange() {
    this.periodRangeDates = null;
  }

  cancelPeriodRange() {
    this.clearPeriodRange();
    this.periodDropdown?.hide();
  }

  onScoreDevelopmentChip(uuid: string | null) {
    this.activeChipUuid = uuid;
    this.requestPayload = {...this.requestPayload, timedQuestionCollectionUuid: uuid};
    this.teamDynamicFacade.loadScoreDevelopment(this.requestPayload);
  }

  onTeamOverviewFilters(filters: TeamOverviewFilters) {
    this.requestPayload = {
      ...this.requestPayload,
      teamsCollectionUuid: filters.collectionUuid,
      teamsSort: filters.sort
    };
    this.teamDynamicFacade.reloadTeamOverview(this.requestPayload);
  }

  onStatementFilters(filters: StatementFilters) {
    this.requestPayload = {
      ...this.requestPayload,
      statementsSort: filters.sort,
      statementsStartDate: filters.startDate,
      statementsEndDate: filters.endDate
    };
    this.teamDynamicFacade.loadStatements(this.requestPayload);
  }

  onStatementOpened(statement: StatementVm) {
    this.teamDynamicFacade.loadStatementResponses(statement);
  }

  onVisibilityChanged(teamVisibility: boolean) {
    this.teamDynamicFacade.setTeamVisibility(teamVisibility);
  }

  private setPeriodOptions() {
    this.periodOptions = [
      {label: this.translate.instant('custom_poll_main_reporting_all_time'), value: 'all'},
      {label: this.translate.instant('wo_team_dynamic_custom_period'), value: 'custom'}
    ];
  }

  private setPeriodLabel() {
    const range = this.periodRangeDates;
    this.periodLabel = this.period === 'custom' && range?.length === 2 ?
      formatDate(range[0], 'dd.MM.yyyy', 'en-US') + ' - ' +
      formatDate(range[1], 'dd.MM.yyyy', 'en-US') :
      this.translate.instant('custom_poll_main_reporting_all_time');
  }
}
